/**
 * PersonaSelectionStep Component
 *
 * A visual persona selector shown as a step in the onboarding wizard.
 * Displays 7 persona cards in a responsive grid. Clicking one selects
 * it with a highlight. Skip defaults to Professional.
 *
 * When `suggestedPersona` is provided (from GitHub quick-scan), shows a
 * confirmation banner above the grid so users can accept in one click.
 *
 * @module components/onboarding/PersonaSelectionStep
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Check, ChevronRight, ChevronLeft, Sparkles, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { getAllPersonas, DEFAULT_PERSONA, getPersonaConfig } from '../../config/personas';

const PersonaSelectionStep = ({
  onSelect,
  onSkip,
  onBack,
  preselected,
  suggestedPersona,
  suggestionConfidence,
  isInferring,
}) => {
  const [selected, setSelected] = useState(preselected || suggestedPersona || null);
  const personas = getAllPersonas();

  // Apply suggestion once inference completes (only if user hasn't manually picked)
  useEffect(() => {
    if (suggestedPersona && !preselected) {
      setSelected(suggestedPersona);
    }
  }, [suggestedPersona, preselected]);

  const handleContinue = () => {
    onSelect(selected || DEFAULT_PERSONA);
  };

  const suggestedConfig = suggestedPersona ? getPersonaConfig(suggestedPersona) : null;
  const showSuggestion = suggestedConfig && !isInferring;
  const confidencePct = Math.round((suggestionConfidence || 0) * 100);

  return (
    <div className="text-center">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">
          How will you use Portfolistic?
        </h2>
        <p className="text-gray-400 max-w-md mx-auto">
          This helps us customize your experience. You can change this anytime.
        </p>
      </div>

      {/* Quick-scan suggestion banner */}
      {isInferring && (
        <div className="flex items-center justify-center gap-2 mb-5 py-2.5 px-4 rounded-xl bg-white/[0.04] border border-white/[0.08] max-w-sm mx-auto">
          <Loader2 className="w-4 h-4 text-emerald-400 animate-spin flex-shrink-0" />
          <span className="text-sm text-gray-400">Reading your GitHub profile...</span>
        </div>
      )}

      {showSuggestion && (
        <div className="mb-5 p-4 rounded-xl bg-emerald-400/[0.08] border border-emerald-400/20 max-w-sm mx-auto text-left">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-400/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">
                Looks like you're a{' '}
                <span className="text-emerald-400">{suggestedConfig.title}</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {confidencePct}% match based on your GitHub profile.{' '}
                <button
                  onClick={() => setSelected(null)}
                  className="text-gray-500 hover:text-gray-300 underline underline-offset-2 transition-colors"
                >
                  Change
                </button>
              </p>
            </div>
            {selected !== suggestedPersona && (
              <button
                onClick={() => setSelected(suggestedPersona)}
                className="text-xs text-emerald-400 hover:text-emerald-300 font-medium flex-shrink-0"
              >
                Select
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6 max-w-3xl mx-auto text-left">
        {personas.map((p) => {
          const Icon = p.icon;
          const isSelected = selected === p.id;
          const isSuggested = p.id === suggestedPersona;
          return (
            <button
              key={p.id}
              onClick={() => setSelected(p.id)}
              className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-primary-500 bg-primary-500/10'
                  : isSuggested
                  ? 'border-emerald-400/30 bg-emerald-400/[0.04] hover:border-emerald-400/50'
                  : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
              }`}
              aria-pressed={isSelected}
            >
              {isSelected && (
                <div className="absolute top-2 right-2 p-1 rounded-full bg-primary-500">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
              {!isSelected && isSuggested && showSuggestion && (
                <div className="absolute top-2 right-2">
                  <Sparkles className="w-3 h-3 text-emerald-400 opacity-70" />
                </div>
              )}
              <Icon
                className={`w-6 h-6 mb-2 ${
                  isSelected ? 'text-primary-400' : isSuggested ? 'text-emerald-400' : 'text-gray-400'
                }`}
              />
              <div className="text-sm font-medium text-white">{p.title}</div>
              <div className="text-xs text-gray-500 mt-1">{p.subtitle}</div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {onBack && (
          <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>
            Back
          </Button>
        )}
        <Button
          variant="gradient"
          size="md"
          onClick={handleContinue}
          disabled={!selected}
          icon={ChevronRight}
          iconPosition="right"
        >
          Continue
        </Button>
        <Button variant="ghost" size="md" onClick={() => onSkip(DEFAULT_PERSONA)}>
          Skip
        </Button>
      </div>
    </div>
  );
};

PersonaSelectionStep.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  preselected: PropTypes.string,
  /** Persona inferred from GitHub quick-scan */
  suggestedPersona: PropTypes.string,
  /** 0–1 confidence from inference */
  suggestionConfidence: PropTypes.number,
  /** True while inference is still running */
  isInferring: PropTypes.bool,
};

export default PersonaSelectionStep;
